import React, { useState, useEffect, useRef } from "react";
import {
  AppBar, Toolbar, Typography, Button, Container, Grid, Box,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField,
  FormControl, InputLabel, Select, MenuItem, Alert, IconButton,
  useMediaQuery, useTheme, Divider,
} from "@mui/material";
import {
  Groups, Map, Assignment, People, Schedule, Notifications,
  Security, SupportAgent, ArrowForward, PlayArrow,
  Dashboard, LocationOn, Checklist, Star, Devices,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import { useTranslation } from "react-i18next";
import Recaptcha from "../../components/Common/Recaptcha";
import { YandexIcon, GoogleIcon } from "../../components/Common/OAuthIcons";
import "../LandingModern.css";

interface Feature {
  icon: React.ReactNode;
  title: string;
  description: string;
}

const LandingBrigades = () => {
  const [isLoginModalOpen, setIsLoginModalOpen] = useState(false);
  const [isRegisterModalOpen, setIsRegisterModalOpen] = useState(false);
  const [loginError, setLoginError] = useState("");
  const [registerError, setRegisterError] = useState("");

  // OAuth на всех доменах
  const showOAuth = true;
  const registerFormRef = useRef<HTMLFormElement>(null);
  const loginFormRef = useRef<HTMLFormElement>(null);
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { login, user, isAuthenticated } = useAuth();
  const [captchaToken, setCaptchaToken] = useState<string | null>(null);
  const captchaKeyRef = useRef(0);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("visible");
          }
        });
      },
      { threshold: 0.1 },
    );
    const elements = document.querySelectorAll(".animate-on-scroll");
    elements.forEach((el) => observer.observe(el));
    return () => {
      elements.forEach((el) => observer.unobserve(el));
    };
  }, []);

  const openLoginModal = () => {
    setIsLoginModalOpen(true);
    document.body.style.overflow = "hidden";
  };

  const closeLoginModal = () => {
    setIsLoginModalOpen(false);
    document.body.style.overflow = "";
    setLoginError("");
  };

  const openRegisterModal = () => {
    setIsRegisterModalOpen(true);
    document.body.style.overflow = "hidden";
  };

  const closeRegisterModal = () => {
    setIsRegisterModalOpen(false);
    document.body.style.overflow = "";
    setRegisterError("");
    setCaptchaToken(null);
    captchaKeyRef.current++;
  };

  const handleLoginSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoginError("");
    if (!loginFormRef.current) { setLoginError(t("login_form_not_found")); return; }
    const form = loginFormRef.current;
    const formData = new FormData(form);
    const email = formData.get("email") as string;
    const password = formData.get("password") as string;
    try {
      const result = await login(email, password);
      if (result.success) {
        closeLoginModal();
        navigate("/fsm/board");
      } else {
        setLoginError(result.error || t("invalid_credentials"));
      }
    } catch (error) {
      setLoginError(t("invalid_credentials"));
    }
  };

  const handleYandexLogin = () => {
    const redirectUrl = encodeURIComponent(window.location.origin + "/fsm/board");
    window.location.href = `/api/v1/auth/yandex?redirect=${redirectUrl}`;
  };

  const handleGoogleLogin = () => {
    const redirectUrl = encodeURIComponent(window.location.origin + "/fsm/board");
    window.location.href = `/api/v1/auth/google?redirect=${redirectUrl}`;
  };

  const handleCaptchaVerify = (token: string) => {
    setCaptchaToken(token);
  };

  const handleRegisterSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setRegisterError("");
    const form = registerFormRef.current;
    if (!form) { setRegisterError(t("registration_form_not_found")); return; }
    if (!captchaToken) { setRegisterError(t("please_complete_captcha")); return; }
    const formData = new FormData(form);
    const name = formData.get("name") as string;
    const email = formData.get("email") as string;
    const password = formData.get("password") as string;
    const confirmPassword = formData.get("confirmPassword") as string;
    const roleSelect = form.querySelector("select") as HTMLSelectElement;
    const role = roleSelect ? roleSelect.value : "dispatcher";
    if (password !== confirmPassword) { setRegisterError(t("passwords_do_not_match")); return; }
    try {
      const response = await fetch("/api/v1/auth/register", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name, email, password, role, captcha_token: captchaToken }),
      });
      const data = await response.json();
      if (response.ok) {
        closeRegisterModal();
        navigate("/fsm/board");
      } else {
        setRegisterError(data.error || t("registration_error"));
      }
    } catch (error) {
      setRegisterError(t("network_error_occurred"));
    }
  };

  const features: Feature[] = [
    {
      icon: <Dashboard sx={{ fontSize: 40 }} />,
      title: t("feature_dispatch_board"),
      description: t("feature_dispatch_board_desc"),
    },
    {
      icon: <Map sx={{ fontSize: 40 }} />,
      title: t("feature_live_map"),
      description: t("feature_live_map_desc"),
    },
    {
      icon: <Groups sx={{ fontSize: 40 }} />,
      title: t("feature_brigade_mgmt"),
      description: t("feature_brigade_mgmt_desc"),
    },
    {
      icon: <People sx={{ fontSize: 40 }} />,
      title: t("feature_worker_profiles"),
      description: t("feature_worker_profiles_desc"),
    },
    {
      icon: <Schedule sx={{ fontSize: 40 }} />,
      title: t("feature_shift_planning"),
      description: t("feature_shift_planning_desc"),
    },
    {
      icon: <Assignment sx={{ fontSize: 40 }} />,
      title: t("feature_orders_reports"),
      description: t("feature_orders_reports_desc"),
    },
    {
      icon: <Notifications sx={{ fontSize: 40 }} />,
      title: t("feature_notifications_chat"),
      description: t("feature_notifications_chat_desc"),
    },
    {
      icon: <Devices sx={{ fontSize: 40 }} />,
      title: t("feature_mobile_apps"),
      description: t("feature_mobile_apps_desc"),
    },
  ];

  const steps = [
    {
      number: "1",
      title: t("step_register"),
      description: t("step_register_desc"),
    },
    {
      number: "2",
      title: t("step_create_brigades"),
      description: t("step_create_brigades_desc"),
    },
    {
      number: "3",
      title: t("step_add_workers"),
      description: t("step_add_workers_desc"),
    },
    {
      number: "4",
      title: t("step_manage_orders"),
      description: t("step_manage_orders_desc"),
    },
  ];

  return (
    <div className="landing-page">
      <AppBar
        position="fixed"
        color="transparent"
        elevation={0}
        sx={{ backdropFilter: "blur(10px)", bgcolor: "rgba(255,255,255,0.9)" }}
      >
        <Toolbar>
          <Box
            component="img"
            src="/logo_dp.webp"
            alt={t("logo_alt")}
            sx={{ height: 40, width: 40, mr: 1.5 }}
          />
          <Typography
            variant="h6"
            component="div"
            sx={{ flexGrow: 1, fontWeight: 800, color: "primary.main" }}
          >
            {t("brand_name")}
          </Typography>

          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <FormControl size="small" sx={{ minWidth: 140 }}>
              <Select
                value={i18n.language}
                onChange={(e) => i18n.changeLanguage(e.target.value)}
                sx={{
                  bgcolor: "rgba(99, 102, 241, 0.15)", borderRadius: "50px",
                  "& fieldset": { border: "none" },
                  "& .MuiSelect-select": { fontWeight: 600, color: "primary.main" },
                }}
              >
                <MenuItem value="ru">{t("langRu")}</MenuItem>
                <MenuItem value="en">{t("langEn")}</MenuItem>
                <MenuItem value="kk">{t("langKk")}</MenuItem>
                <MenuItem value="ky">{t("langKy")}</MenuItem>
                <MenuItem value="tg">{t("langTg")}</MenuItem>
                <MenuItem value="uz">{t("langUz")}</MenuItem>
              </Select>
            </FormControl>

            {isAuthenticated ? (
              <Button
                variant="contained"
                onClick={() => navigate("/fsm/board")}
                sx={{
                  borderRadius: "50px", px: 3,
                  background: "linear-gradient(135deg, #667EEA 0%, #764BA2 100%)",
                  "&:hover": {
                    background: "linear-gradient(135deg, #5568D3 0%, #63408E 100%)",
                    transform: "translateY(-2px)",
                  },
                }}
              >
                {t("home")}
              </Button>
            ) : (
              <>
                <Button
                  onClick={openLoginModal}
                  sx={{
                    borderRadius: "50px", px: 3, color: "primary.main",
                    fontWeight: 600, "&:hover": { bgcolor: "rgba(99, 102, 241, 0.1)" },
                  }}
                >
                  {t("login")}
                </Button>
                <Button
                  variant="contained"
                  onClick={openRegisterModal}
                  sx={{
                    borderRadius: "50px", px: 3,
                    background: "linear-gradient(135deg, #667EEA 0%, #764BA2 100%)",
                    "&:hover": {
                      background: "linear-gradient(135deg, #5568D3 0%, #63408E 100%)",
                      transform: "translateY(-2px)",
                    },
                  }}
                >
                  {t("register")}
                </Button>
              </>
            )}
          </Box>
        </Toolbar>
      </AppBar>

      <Box
        sx={{
          py: 8,
          px: 2,
          textAlign: "center",
          color: "white",
          backgroundImage: `
            linear-gradient(135deg, rgba(25, 118, 210, 0.9) 0%, rgba(33, 150, 243, 0.9) 100%),
            url("/hero_logo.jpg")
          `,
          backgroundSize: "cover",
          backgroundPosition: "center",
          backgroundRepeat: "no-repeat",
          position: "relative",
          overflow: "hidden",
        }}
      >
        <Container maxWidth="lg">
          <Typography variant="h1" component="h1" gutterBottom sx={{ fontWeight: 700, textAlign: "center" }}>
            DispatcherPro - Ваш надежный помощник
          </Typography>
          <Typography variant="h4" component="p" sx={{ mb: 4, opacity: 0.9 }}>
            Онлайн-платформа для эффективного взаимодействия между заказчиками и исполнителями
          </Typography>
          <Box sx={{ mt: 4, display: "flex", justifyContent: "center", gap: 2 }}>
            <Button
              variant="contained"
              size="large"
              color="secondary"
              onClick={() => openRegisterModal()}
              sx={{ px: 4, py: 1.5, fontSize: "1.1rem", boxShadow: 3, "&:hover": { boxShadow: 6 } }}
            >
              {t("startTrial")} <ArrowForward sx={{ ml: 1 }} />
            </Button>
            <Button
              variant="outlined"
              size="large"
              color="inherit"
              onClick={() => navigate("/for-brigades")}
              sx={{ px: 4, py: 1.5, fontSize: "1.1rem", borderWidth: "2px", "&:hover": { borderWidth: "2px" } }}
            >
              Узнать больше
            </Button>
          </Box>
        </Container>
        {/* Декоративные элементы */}
        <Box
          sx={{
            position: "absolute",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            background: `radial-gradient(circle at 10% 20%, rgba(255,255,255,0.1) 0%, transparent 20%),
                         radial-gradient(circle at 90% 80%, rgba(255,255,255,0.1) 0%, transparent 20%)`,
            pointerEvents: "none",
          }}
        />
      </Box>

      {/* Stats */}
      <section className="stats-section">
        <div className="stats-container animate-on-scroll">
          <div className="stat-item">
            <div className="stat-icon"><Groups /></div>
            <div className="stat-number">50+</div>
            <div className="stat-label">{t("statActiveBrigades")}</div>
          </div>
          <div className="stat-item">
            <div className="stat-icon"><Assignment /></div>
            <div className="stat-number">1000+</div>
            <div className="stat-label">{t("statOrdersCompleted")}</div>
          </div>
          <div className="stat-item">
            <div className="stat-icon"><LocationOn /></div>
            <div className="stat-number">15 {t("min")}</div>
            <div className="stat-label">{t("statAvgArrival")}</div>
          </div>
          <div className="stat-item">
            <div className="stat-icon"><Security /></div>
            <div className="stat-number">99.9%</div>
            <div className="stat-label">{t("statUptime")}</div>
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="features-section">
        <div className="section-header animate-on-scroll">
          <div className="section-badge">{t("featuresBadge")}</div>
          <h2 className="section-title">{t("featuresTitle")}</h2>
          <p className="section-subtitle">
            {t("featuresSubtitle")}
          </p>
        </div>
        <div className="features-grid">
          {features.map((feature, index) => (
            <div key={index} className={`feature-card animate-on-scroll delay-${(index % 4) + 1}`}>
              <div className="feature-icon">{feature.icon}</div>
              <h3 className="feature-title">{feature.title}</h3>
              <p className="feature-description">{feature.description}</p>
            </div>
          ))}
        </div>
      </section>

      {/* How It Works */}
      <section className="how-it-works">
        <div className="section-header animate-on-scroll">
          <div className="section-badge">{t("processBadge")}</div>
          <h2 className="section-title">{t("processTitle")}</h2>
          <p className="section-subtitle">
            {t("processSubtitle")}
          </p>
        </div>
        <div className="steps-container">
          {steps.map((step, index) => (
            <div key={index} className={`step-item animate-on-scroll delay-${index + 1}`}>
              <div className="step-number">{step.number}</div>
              <h3 className="step-title">{step.title}</h3>
              <p className="step-description">{step.description}</p>
            </div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="cta-section">
        <div className="cta-content animate-on-scroll">
          <h2 className="cta-title">{t("ctaTitle")}</h2>
          <p className="cta-subtitle">
            {t("ctaSubtitle")}
          </p>
          <div className="cta-buttons">
            <button className="btn-primary" onClick={() => openRegisterModal()}>
              {t("startTrial")} <PlayArrow sx={{ ml: 1 }} />
            </button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="footer">
        <div className="footer-content">
          <div className="footer-section">
            <h3>{t("footerBrand")}</h3>
            <p>{t("footerDescription")}</p>
          </div>
          <div className="footer-section">
            <h3>{t("footerNav")}</h3>
            <p><a href="/fsm/board">{t("dispatchBoard")}</a></p>
            <p><a href="/fsm/pricing">{t("pricing")}</a></p>
          </div>
          <div className="footer-section">
            <h3>{t("footerContacts")}</h3>
            <p>Email: pomogayonline@gmail.com</p>
            <p>{t("phone")}: <a href="tel:+79264602001" style={{ color: "inherit", textDecoration: "none" }}>+7 (926) 460-20-01</a></p>
            <p>{t("inn")}: 230902727737</p>
          </div>
        </div>
        <div className="footer-bottom">
          <p>{t("copyright")}</p>
        </div>
      </footer>

      {/* Login Modal */}
      <Dialog open={isLoginModalOpen} onClose={closeLoginModal} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 700, textAlign: "center" }}>{t("loginTitle")}</DialogTitle>
        <DialogContent>
          {loginError && <Alert severity="error" sx={{ mb: 2 }}>{loginError}</Alert>}
          <form onSubmit={handleLoginSubmit} ref={loginFormRef}>
            <TextField fullWidth label="Email" name="email" type="email" margin="normal" required />
            <TextField fullWidth label={t("password")} name="password" type="password" margin="normal" required />
           </form>
<Divider sx={{ my: 2 }}><Typography variant="body2" color="textSecondary">{t("or")}</Typography></Divider>
          {showOAuth && (
          <>
           <Button fullWidth variant="outlined" onClick={handleYandexLogin} startIcon={<YandexIcon />}
              sx={{ borderColor: "#FC3F1D", color: "#FC3F1D", "&:hover": { borderColor: "#d32f2f", backgroundColor: "rgba(252,63,29,0.04)" } }}>
              {t("loginViaYandex")}
            </Button>
            <Button fullWidth variant="outlined" onClick={handleGoogleLogin} sx={{ mt: 1 }} startIcon={<GoogleIcon />}>
              {t("loginViaGoogle")}
            </Button>
          </>
          )}
        </DialogContent>
        <DialogActions sx={{ justifyContent: "center", pb: 3 }}>
          <Button onClick={closeLoginModal}>{t("cancel")}</Button>
          <Button variant="contained" onClick={handleLoginSubmit} sx={{ borderRadius: "50px", px: 4 }}>{t("login")}</Button>
        </DialogActions>
      </Dialog>

      {/* Register Modal */}
      <Dialog open={isRegisterModalOpen} onClose={closeRegisterModal} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 700, textAlign: "center" }}>{t("registerTitle")}</DialogTitle>
        <DialogContent>
          {registerError && <Alert severity="error" sx={{ mb: 2 }}>{registerError}</Alert>}
          <form onSubmit={handleRegisterSubmit} ref={registerFormRef}>
            <TextField fullWidth label={t("name")} name="name" margin="normal" required />
            <TextField fullWidth label="Email" name="email" type="email" margin="normal" required />
            <TextField fullWidth label={t("password")} name="password" type="password" margin="normal" required />
            <TextField fullWidth label={t("confirmPassword")} name="confirmPassword" type="password" margin="normal" required />
            <FormControl fullWidth margin="normal">
              <InputLabel>{t("role")}</InputLabel>
              <Select defaultValue="dispatcher" label={t("role")} name="role">
                <MenuItem value="dispatcher">{t("roleDispatcherForBrigades")}</MenuItem>
                <MenuItem value="client">{t("roleClient")}</MenuItem>
                <MenuItem value="performer">{t("rolePerformer")}</MenuItem>
              </Select>
            </FormControl>
            <Recaptcha key={captchaKeyRef.current} onVerify={handleCaptchaVerify} />
          </form>
<Divider sx={{ my: 2 }}><Typography variant="body2" color="textSecondary">{t("or")}</Typography></Divider>
            {showOAuth && (
              <>
                <Button fullWidth variant="outlined" onClick={handleYandexLogin} startIcon={<YandexIcon />}
                  sx={{ borderColor: "#FC3F1D", color: "#FC3F1D", "&:hover": { borderColor: "#d32f2f", backgroundColor: "rgba(252,63,29,0.04)" } }}>
                  {t("registerViaYandex")}
                </Button>
                <Button fullWidth variant="outlined" onClick={handleGoogleLogin} sx={{ mt: 1 }} startIcon={<GoogleIcon />}>
                  {t("registerViaGoogle")}
                </Button>
              </>
            )}
        </DialogContent>
        <DialogActions sx={{ justifyContent: "center", pb: 3 }}>
          <Button onClick={closeRegisterModal}>{t("cancel")}</Button>
          <Button variant="contained" onClick={handleRegisterSubmit} sx={{ borderRadius: "50px", px: 4 }}>
            {t("register")}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default LandingBrigades;
